/**
 * Knowledge Hub client — mirrors `apps/api/src/domains/knowledge/routes.ts`
 * verbatim (Task 19; ADR 0008).
 *
 * Permission split (disjoint keys, checked at the route boundary):
 *
 *   knowledge:read    — read articles/versions/attachments, acknowledge a
 *                        version, and read your own ack state
 *   knowledge:write   — create articles, add versions, add attachments
 *   knowledge:publish — publish a version
 *   knowledge:manage  — archive an article, and read WHO has acknowledged
 *
 * TWO THINGS ABOUT THIS DOMAIN CHANGE WHAT A SCREEN CAN ASSUME.
 *
 * An edit is a NEW VERSION, never an overwrite. {@link update} changes only an
 * article's metadata — title, program, audience; the body of a version is
 * immutable once written, so "save my edit" is {@link addVersion} followed by
 * {@link publish}, and the previously published version stays readable.
 *
 * And WHAT YOU SEE DEPENDS ON WHETHER YOU AUTHOR. A caller holding any of
 * `:write`/`:publish`/`:manage` reads the whole workspace including drafts; a
 * plain `knowledge:read` holder (the worker role) sees only published articles
 * targeted at their programs and audience bands. The server decides that from
 * the session — the client never asks for a scope and must not try to reproduce
 * the predicate, which has already moved once.
 *
 * ATTACHMENTS CARRY A REFERENCE, NEVER A URL. {@link addAttachment} takes
 * `storageRef`, a credential-free object path inside the organization's own
 * namespace; the API refuses anything that looks like a URL or that walks out
 * of the namespace with `.` / `..` segments. A readable link is minted on
 * demand and never persisted — {@link attachmentUrl} returns one with the
 * moment it expires, so a screen must fetch it at the point of use rather than
 * caching it.
 */
import { del, get, patch, post } from './transport.js';
// --- articles --------------------------------------------------------------
/**
 * `GET /knowledge/articles` — scoped server-side to what the caller may read,
 * ordered by title (case-insensitive).
 */
export async function articles(query = {}, options) {
    const body = await get('/knowledge/articles', {
        ...options,
        query: { ...query },
    });
    return body.articles;
}
/**
 * `GET /knowledge/articles/:id`.
 *
 * 404 — not 403 — when the article exists but is outside the caller's reading
 * scope, so a worker cannot use this to learn that a draft exists.
 */
export async function article(id, options) {
    const body = await get(`/knowledge/articles/${id}`, options);
    return body.article;
}
/**
 * `POST /knowledge/articles` — needs `knowledge:write`. Creates the article and
 * its first version in one transaction, and returns both. The article starts in
 * `draft` with no current version: publishing is a separate, separately
 * permissioned action.
 */
export function createArticle(input, options) {
    return post('/knowledge/articles', input, options);
}
/**
 * `PATCH /knowledge/articles/:id` — needs `knowledge:write`. Metadata only;
 * content is versioned and cannot be edited in place.
 */
export function update(id, input, options) {
    return patch(`/knowledge/articles/${id}`, input, options);
}
/**
 * `DELETE /knowledge/articles/:id` — needs `knowledge:manage`. ARCHIVES the
 * article rather than deleting it: versions, attachments and acknowledgements
 * all survive, and a reader simply stops being served it.
 */
export async function archive(id, options) {
    await del(`/knowledge/articles/${id}`, options);
}
// --- versions --------------------------------------------------------------
/**
 * `GET /knowledge/articles/:id/versions` — highest version number first,
 * scoped like the article itself.
 */
export async function versions(articleId, options) {
    const body = await get(`/knowledge/articles/${articleId}/versions`, options);
    return body.versions;
}
/**
 * `POST /knowledge/articles/:id/versions` — needs `knowledge:write`. Writes the
 * next version. It is NOT published by that act: readers keep seeing the
 * current version until {@link publish} points the article at this one.
 */
export async function addVersion(articleId, body, options) {
    const payload = await post(`/knowledge/articles/${articleId}/versions`, { body }, options);
    return payload.version;
}
/**
 * `POST /knowledge/articles/:id/publish` — needs `knowledge:publish`. Points the
 * article at `versionId` and sets it published.
 *
 * ANY EXISTING ACKNOWLEDGEMENTS DO NOT CARRY FORWARD. An ack is bound to the
 * version it was given for, so publishing a new version means everyone who must
 * acknowledge this article has to acknowledge it again.
 */
export function publish(articleId, versionId, options) {
    return post(`/knowledge/articles/${articleId}/publish`, { versionId }, options);
}
// --- attachments -----------------------------------------------------------
/** `GET /knowledge/versions/:versionId/attachments` — oldest first. */
export async function attachments(versionId, options) {
    const body = await get(`/knowledge/versions/${versionId}/attachments`, options);
    return body.attachments;
}
/**
 * `POST /knowledge/versions/:versionId/attachments` — needs `knowledge:write`.
 * Records an already-stored object against a version. This does not upload
 * anything: the bytes are put in storage out of band and only the reference is
 * filed here.
 */
export async function addAttachment(versionId, input, options) {
    const body = await post(`/knowledge/versions/${versionId}/attachments`, input, options);
    return body.attachment;
}
/**
 * `GET /knowledge/attachments/:id/url` — mints a short-lived signed read URL.
 *
 * Fetch it at the point of use and DO NOT CACHE IT. The URL is honoured by
 * storage with no further authorization until `expiresAt`, which is exactly why
 * the API never persists one and re-checks the organization's namespace before
 * minting.
 */
export function attachmentUrl(attachmentId, options) {
    return get(`/knowledge/attachments/${attachmentId}/url`, options);
}
// --- acknowledgement -------------------------------------------------------
/**
 * `POST /knowledge/versions/:versionId/ack` — needs `knowledge:read`; records
 * the ACTING user's acknowledgement of this exact version.
 *
 * Idempotent: acknowledging twice returns the existing record and audits
 * nothing the second time. You may only acknowledge a version you were allowed
 * to read — otherwise this would be both a read oracle and a record that
 * somebody accepted a policy they could never have opened.
 */
export async function acknowledge(versionId, options) {
    const body = await post(`/knowledge/versions/${versionId}/ack`, {}, options);
    return body.ack;
}
/** `GET /knowledge/versions/:versionId/ack` — whether the CALLER has acked it. */
export async function hasAcknowledged(versionId, options) {
    const body = await get(`/knowledge/versions/${versionId}/ack`, options);
    return body.acknowledged;
}
/**
 * `GET /knowledge/versions/:versionId/acks` — needs `knowledge:manage`. Who has
 * acknowledged this version, and when. This is the compliance view; the
 * singular {@link hasAcknowledged} is the one a worker's own screen uses.
 */
export async function acks(versionId, options) {
    const body = await get(`/knowledge/versions/${versionId}/acks`, options);
    return body.acks;
}
//# sourceMappingURL=knowledge.js.map